"use client";

import { c as _c } from "react/compiler-runtime";
import type { GuideAction, GuideConfig } from "@/config/guides/types";
import { guidesConfig } from "@/config/guides";
import { useAIChatStore } from "@/stores/aiChatStore";
import { useConnectorsStore } from "@/stores/connectorsStore";
import { useGuideStore } from "@/stores/guideStore";
import { useSidebarStore } from "@/stores/sidebarStore";
import { driver, type Driver, type DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import { usePathname, useRouter } from "next/navigation";
import { createContext, useCallback, useContext, useEffect, useRef, type ReactNode } from "react";
function executeGuideAction(action: GuideAction) {
  switch (action) {
    case "openAIPanel":
      useAIChatStore.getState().openPanel();
      break;
    case "closeAIPanel":
      useAIChatStore.getState().closePanel();
      break;
    case "expandSidebar":
      useSidebarStore.getState().setWorkflowExpanded(true);
      break;
    case "switchToMyConnectors":
      useConnectorsStore.getState().setActiveTab("configured");
      break;
  }
}
interface GuideContextValue {
  startGuide: (guideId: string) => void;
  endGuide: () => void;
  isRunning: boolean;
  availableGuides: GuideConfig[];
  hasCompleted: (guideId: string) => boolean;
}
const GuideContext = createContext<GuideContextValue | null>(null);
function waitForElement(selector: string, timeout: number = 5000): Promise<Element | null> {
  return new Promise(resolve => {
    const element = document.querySelector(selector);
    if (element) {
      resolve(element);
      return;
    }
    const observer = new MutationObserver(() => {
      const element = document.querySelector(selector);
      if (element) {
        observer.disconnect();
        resolve(element);
      }
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    setTimeout(() => {
      observer.disconnect();
      resolve(null);
    }, timeout);
  });
}
interface GuideProviderProps {
  children: ReactNode;
}
export function GuideProvider(t0) {
  const $ = _c(29);
  const {
    children
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const driverRef = useRef(null);
  const isNavigatingRef = useRef(false);
  const {
    activeGuide,
    isRunning,
    pendingNavigation,
    startGuide: storeStartGuide,
    endGuide: storeEndGuide,
    clearPendingNavigation,
    hasCompleted
  } = useGuideStore();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = guidesConfig.guides as GuideConfig[] || [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const availableGuides = t1;
  let t2;
  if ($[1] !== pathname || $[2] !== router || $[3] !== storeEndGuide) {
    t2 = (guide, t3) => {
      const startIndex = t3 === undefined ? 0 : t3;
      if (driverRef.current) {
        isNavigatingRef.current = true;
        driverRef.current.destroy();
        isNavigatingRef.current = false;
      }
      const steps = guide.steps.map(_temp);
      const driverInstance = driver({
        showProgress: guide.showProgress ?? false,
        steps,
        animate: true,
        allowClose: true,
        overlayClickBehavior: "close",
        stagePadding: 10,
        stageRadius: 8,
        popoverClass: "b3os-guide-popover",
        onDestroyed: () => {
          if (!isNavigatingRef.current) {
            storeEndGuide();
          }
          driverRef.current = null;
        },
        onNextClick: () => {
          const nextStepIndex = (driverInstance.getActiveIndex() ?? 0) + 1;
          const nextStepConfig = guide.steps[nextStepIndex];
          if (nextStepConfig?.route && nextStepConfig.route !== pathname) {
            isNavigatingRef.current = true;
            driverInstance.destroy();
            isNavigatingRef.current = false;
            router.push(nextStepConfig.route);
            useGuideStore.setState({
              pendingNavigation: {
                route: nextStepConfig.route,
                stepIndex: nextStepIndex
              }
            });
          } else {
            driverInstance.moveNext();
          }
        },
        onPrevClick: () => {
          const prevStepIndex = (driverInstance.getActiveIndex() ?? 0) - 1;
          const prevStepConfig = guide.steps[prevStepIndex];
          if (prevStepConfig?.route && prevStepConfig.route !== pathname) {
            isNavigatingRef.current = true;
            driverInstance.destroy();
            isNavigatingRef.current = false;
            router.push(prevStepConfig.route);
            useGuideStore.setState({
              pendingNavigation: {
                route: prevStepConfig.route,
                stepIndex: prevStepIndex
              }
            });
          } else {
            driverInstance.movePrevious();
          }
        }
      });
      driverRef.current = driverInstance;
      driverInstance.drive(startIndex);
      setTimeout(() => {
        driverInstance.refresh();
      }, 100);
    };
    $[1] = pathname;
    $[2] = router;
    $[3] = storeEndGuide;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const initializeDriver = t2;
  let t3;
  if ($[5] !== initializeDriver || $[6] !== pathname || $[7] !== router || $[8] !== storeStartGuide) {
    t3 = guideId => {
      const guide_0 = availableGuides.find(g => g.id === guideId);
      if (!guide_0) {
        console.warn(`Guide with id "${guideId}" not found`);
        return;
      }
      if (guide_0.startRoute && guide_0.startRoute !== pathname) {
        storeStartGuide(guide_0);
        router.push(guide_0.startRoute);
        useGuideStore.setState({
          pendingNavigation: {
            route: guide_0.startRoute,
            stepIndex: 0
          }
        });
      } else {
        storeStartGuide(guide_0);
        initializeDriver(guide_0, 0);
      }
    };
    $[5] = initializeDriver;
    $[6] = pathname;
    $[7] = router;
    $[8] = storeStartGuide;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const startGuide = t3;
  let t4;
  if ($[10] !== storeEndGuide) {
    t4 = () => {
      if (driverRef.current) {
        driverRef.current.destroy();
      }
      storeEndGuide();
    };
    $[10] = storeEndGuide;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const endGuide = t4;
  let t5;
  let t6;
  if ($[12] !== activeGuide || $[13] !== clearPendingNavigation || $[14] !== initializeDriver || $[15] !== pathname || $[16] !== pendingNavigation) {
    t5 = () => {
      if (pendingNavigation && activeGuide && pendingNavigation.route === pathname) {
        const handleNavigation = async () => {
          const step_0 = activeGuide.steps[pendingNavigation.stepIndex];
          if (step_0?.waitForElement) {
            const timeout_0 = typeof step_0.waitForElement === "number" ? step_0.waitForElement : 5000;
            await waitForElement(step_0.element, timeout_0);
          } else {
            await new Promise(_temp2);
          }
          await new Promise(_temp3);
          clearPendingNavigation();
          initializeDriver(activeGuide, pendingNavigation.stepIndex);
        };
        handleNavigation();
      }
    };
    t6 = [pathname, pendingNavigation, activeGuide, clearPendingNavigation, initializeDriver];
    $[12] = activeGuide;
    $[13] = clearPendingNavigation;
    $[14] = initializeDriver;
    $[15] = pathname;
    $[16] = pendingNavigation;
    $[17] = t5;
    $[18] = t6;
  } else {
    t5 = $[17];
    t6 = $[18];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => () => {
      if (driverRef.current) {
        isNavigatingRef.current = true;
        driverRef.current.destroy();
        isNavigatingRef.current = false;
      }
    };
    t8 = [];
    $[19] = t7;
    $[20] = t8;
  } else {
    t7 = $[19];
    t8 = $[20];
  }
  useEffect(t7, t8);
  let t9;
  if ($[21] !== endGuide || $[22] !== hasCompleted || $[23] !== isRunning || $[24] !== startGuide) {
    t9 = {
      startGuide,
      endGuide,
      isRunning,
      availableGuides,
      hasCompleted
    };
    $[21] = endGuide;
    $[22] = hasCompleted;
    $[23] = isRunning;
    $[24] = startGuide;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  const contextValue = t9;
  let t10;
  if ($[26] !== children || $[27] !== contextValue) {
    t10 = <GuideContext.Provider value={contextValue}>{children}</GuideContext.Provider>;
    $[26] = children;
    $[27] = contextValue;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  return t10;
}
function _temp3(resolve_0) {
  return setTimeout(resolve_0, 150);
}
function _temp2(resolve) {
  return setTimeout(resolve, 100);
}
function _temp(step) {
  return {
    element: step.element,
    popover: {
      title: step.popover.title,
      description: step.popover.description,
      side: step.popover.side,
      align: step.popover.align
    },
    onHighlightStarted: async () => {
      if (step.action) {
        executeGuideAction(step.action);
      }
      if (step.waitForElement) {
        const timeout = typeof step.waitForElement === "number" ? step.waitForElement : 5000;
        await waitForElement(step.element, timeout);
      }
    }
  };
}
export function useGuideContext() {
  const context = useContext(GuideContext);
  if (!context) {
    throw new Error("useGuideContext must be used within a GuideProvider");
  }
  return context;
}